/*
 * The reading pane: the whole conversation as white cards on a light gray
 * pane, older messages collapsed. HTML mail sits in a sandboxed frame;
 * remote images stay hidden until asked for.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Menu, MenuContent, MenuItem, MenuTrigger } from "../ui/Menu";
import { SEGMENT_OFF, SEGMENT_ON } from "../ui/segment";
import { cx } from "../lib/cx";
import { Dialog } from "../ui/Dialog";
import { IconAlert, IconAlertCircle, IconCode, IconDownload, IconMore, IconRefresh, Spinner } from "../ui/icons";
import { Button, buttonClass } from "../ui/Button";
import { Tooltip } from "../ui/Tooltip";
import { ApiError, attachmentUrl, fetchAttachment, rawMessageUrl } from "./api";
import { displayName, fileSize, fullDate, listDate, plural } from "./format";
import {
  IconArchiveBox,
  IconBack,
  IconBan,
  IconFolderMove,
  IconFileText,
  IconForward,
  IconImage,
  IconMail,
  IconNotSpam,
  IconPaperclip,
  IconReply,
  IconReplyAll,
  IconSpam,
  IconStar,
  IconStarFilled,
  IconTrashCan,
} from "./icons";
import { MailFrame } from "./MailFrame";
import { Empty, SenderAvatar } from "./parts";
import { IconAlarm, IconBell, IconBellOff, IconCalendar, IconTag } from "./icons";
import { LabelItems, LaterItems, type LaterChoice } from "./later";
import type { Addr, Attachment, Conversation, Invite, InviteAnswer, Label, Message, PartStat } from "./types";

export type PaneActions = {
  back: () => void;
  archive: () => void;
  spam: () => void;
  trash: () => void;
  markUnread: () => void;
  move: () => void;
  star: () => void;
  block: (sender: Addr) => void;
  reply: (m: Message, mode: "reply" | "replyAll" | "forward") => void;
  showImages: () => void;
  allowSender: (sender: Addr) => void;
  /** Snooze the open conversation; null where it can't be (Scheduled, Snoozed itself). */
  snooze: { choices: LaterChoice[]; onPick: (iso: string) => void; onCustom: () => void } | null;
  /** Labels on the open conversation. */
  labels: { list: Label[]; has: (id: string) => "all" | "some" | "none"; onToggle: (id: string, on: boolean) => void; onManage: () => void } | null;
  /** Mute or unmute the open conversation. */
  mute: (on: boolean) => void;
  /** Answers an invitation (sends the reply from this mailbox); resolves with the invitation as it is now. */
  answerInvite: (m: Message, answer: InviteAnswer) => Promise<Invite>;
};

function ToolButton({ label, onClick, children, danger, className }: { label: string; onClick: () => void; children: ReactNode; danger?: boolean; className?: string }) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={cx("inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[13px] text-body transition-colors", danger ? "hover:bg-bad-bg hover:text-bad" : "hover:bg-hover hover:text-fg", className)}
      >
        {children}
      </button>
    </Tooltip>
  );
}

export function ReadingPane({
  mb,
  me,
  data,
  loading,
  restLoading,
  restError,
  error,
  onRetry,
  role,
  starred,
  images,
  actions,
  empty,
  labelNames = [],
  muted = false,
}: {
  /** The open conversation is muted. */
  muted?: boolean;
  mb: string;
  me: string;
  /** The labels on this conversation, by name. */
  labelNames?: string[];
  data: Conversation | undefined;
  loading: boolean;
  /** The clicked message is shown; the rest of its conversation is still coming. */
  restLoading: boolean;
  restError: string | null;
  error: Error | null;
  onRetry: () => void;
  role: string | null;
  starred: boolean;
  images: boolean;
  actions: PaneActions;
  empty: string | null;
}) {
  if (empty !== null) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        {empty ? (
          <Empty icon={<IconMail size={20} />} title="Nothing open">
            {empty}
          </Empty>
        ) : null}
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Empty
          icon={<IconAlert size={20} />}
          title="This message didn't load"
          action={
            <div className="flex flex-col items-center gap-3">
              <Button variant="primary" size="sm" leading={<IconRefresh size={14} />} onClick={onRetry}>
                Try again
              </Button>
              <button type="button" onClick={actions.back} className="text-[13px] text-link hover:underline">
                Back to the list
              </button>
            </div>
          }
        >
          {error.message}
        </Empty>
      </div>
    );
  }
  const inSpam = role === "junk";
  const inTrash = role === "trash";
  const last = data?.messages.at(-1);
  return (
    <div className="relative">
      <div className="sticky top-0 z-20 flex items-center gap-0.5 border-b border-line bg-surface px-3 py-2 lg:px-5">
        <ToolButton label="Back to the list" onClick={actions.back} className="mr-1 px-1.5 lg:hidden">
          <IconBack size={16} />
        </ToolButton>
        <ToolButton label="Archive (e)" onClick={actions.archive}>
          <IconArchiveBox size={15} />
        </ToolButton>
        <ToolButton label={inSpam ? "Not spam (!)" : "Spam (!)"} onClick={actions.spam}>
          {inSpam ? <IconNotSpam size={15} /> : <IconSpam size={15} />}
        </ToolButton>
        {last?.from && last.from.address !== me ? (
          <ToolButton label="Block sender" onClick={() => actions.block(last.from!)} danger>
            <IconBan size={15} />
          </ToolButton>
        ) : null}
        <ToolButton label={inTrash || inSpam ? "Delete forever (#)" : "Delete (#)"} onClick={actions.trash} danger>
          <IconTrashCan size={15} />
        </ToolButton>
        <span className="mx-1 h-6 w-px bg-line" />
        <ToolButton label="Mark unread (Shift+U)" onClick={actions.markUnread}>
          <IconMail size={15} />
        </ToolButton>
        <ToolButton label="Move to folder (v)" onClick={actions.move}>
          <IconFolderMove size={15} />
        </ToolButton>
        {actions.snooze ? (
          <ToolMenu label="Snooze">
            <IconAlarm size={15} />
            <LaterItems choices={actions.snooze.choices} onPick={actions.snooze.onPick} onCustom={actions.snooze.onCustom} />
          </ToolMenu>
        ) : null}
        <ToolButton label={muted ? "Unmute" : "Mute conversation"} onClick={() => actions.mute(!muted)}>
          {muted ? <IconBell size={15} /> : <IconBellOff size={15} />}
        </ToolButton>
        {actions.labels ? (
          <ToolMenu label="Label">
            <IconTag size={15} />
            <LabelItems labels={actions.labels.list} has={actions.labels.has} onToggle={actions.labels.onToggle} onManage={actions.labels.onManage} />
          </ToolMenu>
        ) : null}
        {loading ? <Spinner size={15} className="ml-auto text-faint" /> : null}
      </div>

      {!data ? (
        <div className="mx-auto max-w-4xl space-y-3 px-4 py-6 lg:px-8" role="status" aria-label="Opening the message">
          <div className="hx-skeleton h-7 w-2/3 rounded" />
          <div className="hx-skeleton h-40 rounded-[var(--radius-card)]" />
        </div>
      ) : (
        <div className="mx-auto max-w-4xl px-4 py-6 lg:px-8">
          <div className="mb-5">
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.01em] break-words text-fg">{data.subject || "(no subject)"}</h1>
              <button
                type="button"
                onClick={actions.star}
                className={cx("mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-hover", starred ? "text-brand-500" : "text-dim hover:text-fg")}
                aria-label={starred ? "Unstar (s)" : "Star (s)"}
                aria-pressed={starred}
              >
                {starred ? <IconStarFilled size={19} /> : <IconStar size={19} />}
              </button>
            </div>
            {data.messages.length > 1 ? <p className="mt-1 text-[12.5px] text-dim">{plural(data.messages.length, "message")} in this conversation</p> : null}
            {labelNames.length ? (
              <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Labels">
                {labelNames.map((n) => (
                  <span key={n} className="inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-[12px] text-body shadow-[inset_0_0_0_1px_var(--hx-line-strong)]">
                    <IconTag size={11} className="text-dim" />
                    {n}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          {/* Keyed so the full conversation, when it arrives, starts with the right messages open. */}
          <Thread key={data.messages.length > 1 ? "all" : "one"} mb={mb} me={me} messages={data.messages} images={images} actions={actions} />
          {restLoading ? (
            <p className="mt-3 inline-flex items-center gap-2 text-[12px] text-dim" role="status">
              <Spinner size={12} />
              Finding the rest of this conversation…
            </p>
          ) : restError ? (
            <p className="mt-3 text-[12px] text-dim">
              The rest of this conversation didn't load: {restError}{" "}
              <button type="button" onClick={onRetry} className="text-link hover:underline">
                Try again
              </button>
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** A toolbar button that opens a menu: the first child is its icon, the second the menu's items. */
function ToolMenu({ label, children }: { label: string; children: [ReactNode, ReactNode] }) {
  return (
    <Menu>
      <Tooltip content={label}>
        <MenuTrigger aria-label={label} className="inline-flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-body transition-colors hover:bg-hover hover:text-fg data-[state=open]:bg-hover">
          {children[0]}
        </MenuTrigger>
      </Tooltip>
      <MenuContent align="start" className="w-64">
        {children[1]}
      </MenuContent>
    </Menu>
  );
}

const STATUS_WORD: Record<string, string> = { accepted: "Going", tentative: "Maybe", declined: "Not going", "needs-action": "Hasn't answered", delegated: "Passed it on" };
const statusWord = (s: PartStat | null) => (s ? (STATUS_WORD[s] ?? s) : "Hasn't answered");

/** "Tue, Oct 22, 2:00 PM – 3:00 PM", in this browser's time zone; all-day events by date. */
function inviteWhen(inv: Invite): string {
  if (!inv.start) return "No time given";
  const s = new Date(inv.start);
  const e = inv.end ? new Date(inv.end) : null;
  if (inv.allDay) {
    const day = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
    // An all-day event ends at the next midnight: one day unless it spans more.
    const last = e ? new Date(e.getTime() - 86_400_000) : null;
    return last && last.getTime() > s.getTime() ? `${day(s)} to ${day(last)}, all day` : `${day(s)}, all day`;
  }
  const date = s.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (!e) return `${date}, ${time(s)}`;
  const sameDay = e.toDateString() === s.toDateString();
  return sameDay ? `${date}, ${time(s)} to ${time(e)}` : `${date}, ${time(s)} to ${e.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}, ${time(e)}`;
}

/**
 * A calendar invitation at the top of its message: what, when (in this browser's time zone), where, who sent it and
 * who's coming, your answer, and Accept, Maybe and Decline, which send the reply from this mailbox. Refusals are said
 * as the API says them.
 */
function InviteCard({ m, me, onAnswer }: { m: Message; me: string; onAnswer: (answer: InviteAnswer) => Promise<Invite> }) {
  const [inv, setInv] = useState<Invite>(m.invite!);
  const [busy, setBusy] = useState<InviteAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canceled = inv.method === "CANCEL";
  const answerable = inv.method === "REQUEST" && !!inv.organizer;
  const others = inv.attendees.filter((a) => a.address !== me);
  const shown = others.slice(0, 6);
  const answer = async (a: InviteAnswer) => {
    setBusy(a);
    setError(null);
    try {
      setInv(await onAnswer(a));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The answer wasn't sent. Try again.");
    } finally {
      setBusy(null);
    }
  };
  const choices: [InviteAnswer, string][] = [
    ["accepted", "Accept"],
    ["tentative", "Maybe"],
    ["declined", "Decline"],
  ];
  return (
    <section aria-label="Invitation" className="border-b border-line-soft px-5 py-4">
      <div className="flex items-start gap-3">
        <IconCalendar size={18} className="mt-0.5 shrink-0 text-dim" />
        <div className="min-w-0 flex-1">
          <p className={cx("text-[15px] font-semibold text-fg", canceled && "line-through decoration-faint")}>{inv.summary || "Invitation"}</p>
          {canceled ? <p className="mt-0.5 text-[13px] text-dim">This event was canceled.</p> : null}
          <dl className="mt-2 grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]">
            <dt className="text-dim">When</dt>
            <dd className="text-fg">
              {inviteWhen(inv)}
              {inv.recurring ? <span className="text-dim"> · repeats</span> : null}
            </dd>
            {inv.location ? (
              <>
                <dt className="text-dim">Where</dt>
                <dd className="break-words text-fg">{inv.location}</dd>
              </>
            ) : null}
            {inv.organizer ? (
              <>
                <dt className="text-dim">From</dt>
                <dd className="text-fg">
                  {inv.organizer.name ? `${inv.organizer.name} ` : ""}
                  <span className="text-[12.5px] text-dim">{inv.organizer.address}</span>
                </dd>
              </>
            ) : null}
            {others.length ? (
              <>
                <dt className="text-dim">Who's coming</dt>
                <dd className="text-fg">
                  <ul className="space-y-0.5">
                    {shown.map((a) => (
                      <li key={a.address}>
                        {a.name || a.address} <span className="text-dim">· {statusWord(a.status)}</span>
                      </li>
                    ))}
                  </ul>
                  {others.length > shown.length ? <p className="text-dim">and {plural(others.length - shown.length, "other")}</p> : null}
                </dd>
              </>
            ) : null}
            {answerable ? (
              <>
                <dt className="text-dim">You</dt>
                <dd className="text-fg">{statusWord(inv.myStatus)}</dd>
              </>
            ) : null}
          </dl>
          {answerable && !canceled ? (
            <div role="group" aria-label="Your answer" className="mt-3 inline-flex rounded-[9px] bg-surface p-0.5 shadow-[inset_0_0_0_1px_var(--hx-line-strong)]">
              {choices.map(([a, label]) => (
                <button
                  key={a}
                  type="button"
                  aria-pressed={inv.myStatus === a}
                  disabled={!!busy}
                  onClick={() => void answer(a)}
                  className={cx("inline-flex h-8 items-center gap-1.5 rounded-[7px] px-3 text-[13px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-brand-500 disabled:opacity-60", inv.myStatus === a ? SEGMENT_ON : SEGMENT_OFF)}
                >
                  {busy === a ? <Spinner size={12} /> : null}
                  {label}
                </button>
              ))}
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="mt-2 flex items-start gap-1.5 text-[13px] text-bad">
              <IconAlertCircle size={14} className="mt-0.5 shrink-0" />
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Thread({ mb, me, messages, images, actions }: { mb: string; me: string; messages: Message[]; images: boolean; actions: PaneActions }) {
  // Older messages start collapsed; the newest and any unread ones start open.
  // Decided once per conversation: marking messages read (which reloads it) mustn't fold them away.
  const [open, setOpen] = useState(() => new Set(messages.filter((m, i) => i === messages.length - 1 || !m.flags.seen).map((m) => `${m.folder}:${m.uid}`)));
  return (
    <div className="space-y-3">
      {messages.map((m, i) => {
        const key = `${m.folder}:${m.uid}`;
        const expanded = open.has(key);
        return (
          <MessageCard
            key={key}
            mb={mb}
            me={me}
            m={m}
            expanded={expanded || i === messages.length - 1}
            latest={i === messages.length - 1}
            images={images}
            actions={actions}
            onToggle={() =>
              setOpen((s) => {
                const n = new Set(s);
                if (n.has(key)) n.delete(key);
                else n.add(key);
                return n;
              })
            }
          />
        );
      })}
    </div>
  );
}

const addrList = (xs: Addr[]) => xs.map((x) => x.address).join(", ");

function MessageCard({ mb, me, m, expanded, latest, images, actions, onToggle }: { mb: string; me: string; m: Message; expanded: boolean; latest: boolean; images: boolean; actions: PaneActions; onToggle: () => void }) {
  const from = m.from;
  const recipients = [...m.to, ...m.cc];
  const snippet = (m.text ?? "").replace(/\s+/g, " ").slice(0, 160);
  return (
    <article className="card overflow-hidden" aria-label={`From ${displayName(from)}`}>
      <header className={cx("flex items-start gap-3 px-5 py-3.5", expanded && "border-b border-line-soft", !latest && "cursor-pointer hover:bg-hover/50")} onClick={latest ? undefined : onToggle}>
        <SenderAvatar address={from?.address ?? "?"} name={from?.name ?? null} size={36} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[14px] font-semibold text-fg">{from?.address === me ? "You" : displayName(from)}</span>
            {from ? <span className={cx("text-[12.5px] break-all text-dim", !expanded && "hidden sm:inline")}>{from.address}</span> : null}
          </div>
          {expanded ? (
            <div className="mt-0.5 text-[12.5px] text-dim">
              {recipients.length ? (
                <>
                  to <span>{addrList(m.to)}</span>
                  {m.cc.length ? (
                    <>
                      , cc <span>{addrList(m.cc)}</span>
                    </>
                  ) : null}
                  {m.bcc.length ? (
                    <>
                      , bcc <span>{addrList(m.bcc)}</span>
                    </>
                  ) : null}
                  <span className="mx-1 text-faint">·</span>
                </>
              ) : null}
              <span>{fullDate(m.date)}</span>
            </div>
          ) : (
            <p className="mt-0.5 truncate text-[13px] text-dim">{snippet || (m.attachments.length ? plural(m.attachments.length, "attachment") : "")}</p>
          )}
        </div>
        {!expanded ? (
          <span className="shrink-0 text-[12px] text-dim" title={fullDate(m.date)}>
            {listDate(m.date)}
          </span>
        ) : (
          <MessageMenu mb={mb} m={m} />
        )}
      </header>

      {expanded ? (
        <>
          {m.invite ? <InviteCard m={m} me={me} onAnswer={(a) => actions.answerInvite(m, a)} /> : null}
          {m.warning ? (
            // A problem, said plainly: rose with an icon and words, as in the panel.
            <div role="alert" className="flex items-start gap-2.5 border-b border-bad-line bg-bad-bg px-5 py-2.5 text-[13px] text-bad">
              <IconAlertCircle size={15} className="mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold">Be careful with this message. </span>
                {m.warning}
              </span>
            </div>
          ) : null}
          {m.remoteImages > 0 && !images ? (
            // A choice, not a problem: a quiet line.
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-soft bg-panel/60 px-5 py-2 text-[12.5px] text-body">
              <IconImage size={14} className="shrink-0 text-dim" />
              <span className="flex-1">Images are hidden, so the sender can't tell you opened this.</span>
              <button type="button" onClick={actions.showImages} className="font-medium text-link hover:underline">
                Show images
              </button>
              {from ? (
                <button type="button" onClick={() => actions.allowSender(from)} className="font-medium text-link hover:underline">
                  Always from this sender
                </button>
              ) : null}
            </div>
          ) : null}
          {m.html !== null ? (
            <MailFrame html={m.html} title={`Message from ${displayName(from)}`} />
          ) : (
            <div className="px-5 py-4 text-[14px] leading-relaxed whitespace-pre-wrap text-fg">
              <Linkified text={m.text ?? ""} />
            </div>
          )}
          {m.attachments.length ? <Attachments mb={mb} m={m} /> : null}
          {latest ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-line-soft px-5 py-3.5">
              <Button size="sm" variant="secondary" leading={<IconReply size={14} />} onClick={() => actions.reply(m, "reply")}>
                Reply
              </Button>
              <Button size="sm" variant="secondary" leading={<IconReplyAll size={14} />} onClick={() => actions.reply(m, "replyAll")}>
                Reply all
              </Button>
              <Button size="sm" variant="secondary" leading={<IconForward size={14} />} onClick={() => actions.reply(m, "forward")}>
                Forward
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

/** A download that never leaves the page, as an attachment's link does. */
function saveFrom(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** One message's own actions: the original as it arrived (headers with the server's SPF, DKIM and DMARC results), and as a file. */
function MessageMenu({ mb, m }: { mb: string; m: Message }) {
  return (
    // Its own clicks: an older message's header folds it on click.
    <span onClick={(e) => e.stopPropagation()} className="-mr-1.5 shrink-0">
      <Menu>
        <Tooltip content="More">
          <MenuTrigger aria-label={`More for the message from ${displayName(m.from)}`} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-dim transition-colors hover:bg-hover hover:text-fg data-[state=open]:bg-hover">
            <IconMore size={18} />
          </MenuTrigger>
        </Tooltip>
        <MenuContent align="end" className="w-56">
          <MenuItem icon={<IconCode size={16} />} onSelect={() => window.open(rawMessageUrl(mb, m.folder, m.uid), "_blank", "noopener")}>
            Show original
          </MenuItem>
          <MenuItem icon={<IconDownload size={16} />} onSelect={() => saveFrom(rawMessageUrl(mb, m.folder, m.uid, true))}>
            Download as .eml
          </MenuItem>
        </MenuContent>
      </Menu>
    </span>
  );
}

const URL_RE = /\bhttps?:\/\/[^\s<>"')\]]+[^\s<>"')\].,;:!?]/gi;

function Linkified({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const i = match.index ?? 0;
    if (i > last) parts.push(text.slice(last, i));
    parts.push(
      <a key={i} href={match[0]} target="_blank" rel="noopener noreferrer nofollow" className="link break-all">
        {match[0]}
      </a>,
    );
    last = i + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/** A file's kind for its chip: a picture, a PDF, or anything else. */
function kindOf(a: Attachment): "image" | "pdf" | "file" {
  if (a.preview === "image" || a.contentType.startsWith("image/")) return "image";
  if (a.preview === "pdf" || a.contentType === "application/pdf") return "pdf";
  return "file";
}

/**
 * Attachments as chips: the kind's icon, the name and size. A picture or a PDF opens a preview (checked by its
 * bytes, shown from a blob); everything else downloads. Every chip has its own download button.
 */
function Attachments({ mb, m }: { mb: string; m: Message }) {
  const [viewing, setViewing] = useState<Attachment | null>(null);
  return (
    <div className="border-t border-line-soft px-5 py-3.5">
      <p className="mb-2 text-[12.5px] text-dim">{plural(m.attachments.length, "attachment")}</p>
      <div className="flex flex-wrap gap-2">
        {m.attachments.map((a) => {
          const kind = kindOf(a);
          const Icon = kind === "image" ? IconImage : kind === "pdf" ? IconFileText : IconPaperclip;
          const face = (
            <>
              <span aria-hidden="true" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-panel text-dim">
                <Icon size={16} />
              </span>
              <span className="min-w-0 text-left">
                <span className="block max-w-[200px] truncate text-[13px] text-fg">{a.filename}</span>
                <span className="block text-[11.5px] text-dim">
                  {kind === "image" ? "Picture" : kind === "pdf" ? "PDF" : "File"} · {fileSize(a.size)}
                </span>
              </span>
            </>
          );
          return (
            <span key={a.part} className="inline-flex items-center overflow-hidden rounded-[10px] bg-surface shadow-[inset_0_0_0_1px_var(--hx-line-strong)]">
              {a.preview ? (
                <button type="button" onClick={() => setViewing(a)} className="inline-flex items-center gap-2.5 py-1.5 pr-3 pl-1.5 hover:bg-hover" title={`Preview ${a.filename}`}>
                  {face}
                </button>
              ) : (
                <a href={attachmentUrl(mb, m.folder, m.uid, a.part)} download className="inline-flex items-center gap-2.5 py-1.5 pr-3 pl-1.5 hover:bg-hover" title={`Download ${a.filename}`}>
                  {face}
                </a>
              )}
              <a href={attachmentUrl(mb, m.folder, m.uid, a.part)} download className="inline-flex h-11 items-center border-l border-line px-2.5 text-dim hover:bg-hover hover:text-fg" aria-label={`Download ${a.filename}`}>
                <IconDownload size={15} />
              </a>
            </span>
          );
        })}
      </div>
      {viewing ? <Viewer mb={mb} m={m} a={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}

/** What a file really is, from its first bytes: the raster images and PDF the viewer shows, or null. */
function sniff(b: Uint8Array): string | null {
  const ascii = (from: number, to: number) => String.fromCharCode(...b.slice(from, to));
  if (b[0] === 0x89 && ascii(1, 4) === "PNG") return "image/png";
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (ascii(0, 4) === "GIF8") return "image/gif";
  if (ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP") return "image/webp";
  if (ascii(0, 5) === "%PDF-") return "application/pdf";
  return null;
}

/** Raster images and PDF only, checked by their bytes and shown from a blob. Nothing else is ever shown inline. */
function Viewer({ mb, m, a, onClose }: { mb: string; m: Message; a: Attachment; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    fetchAttachment(mb, m.folder, m.uid, a.part)
      .then(async (blob) => {
        if (!alive) return;
        // Shown only when the bytes are what the type says: a "PDF" that is really a web page never renders.
        const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
        const type = sniff(head);
        if (!type || (a.preview === "pdf") !== (type === "application/pdf")) throw new Error("This file can't be shown here. Download it instead.");
        made = URL.createObjectURL(new Blob([blob], { type }));
        setUrl(made);
      })
      .catch((err) => alive && setError(err instanceof ApiError || err instanceof Error ? err.message : "This file didn't load."));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [mb, m.folder, m.uid, a.part, a.preview]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={<span className="break-all">{a.filename}</span>} size="xl" align="top">
      <div className="flex min-h-[50vh] items-center justify-center rounded-lg bg-panel">
        {error ? (
          <p className="p-6 text-sm text-dim">{error}</p>
        ) : !url ? (
          <Spinner size={20} className="text-faint" />
        ) : a.preview === "image" ? (
          <img src={url} alt={a.filename} className="max-h-[70vh] max-w-full object-contain" />
        ) : (
          // The browser's own PDF viewer, which runs apart from this page (it can't render inside a sandboxed frame).
          <iframe src={url} title={a.filename} className="h-[70vh] w-full rounded-lg border-0 bg-white" />
        )}
      </div>
      <div className="mt-3 flex justify-end">
        <a href={attachmentUrl(mb, m.folder, m.uid, a.part)} download className={buttonClass("secondary", "sm")}>
          <IconDownload size={14} />
          Download
        </a>
      </div>
    </Dialog>
  );
}
